import { Button } from '@factorialco/deprecated-design-system'
import {
  AtsCreateQuestionTemplateDocument,
  AtsQuestionTemplatesListDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CreateQuestionForm } from 'modules/ats/components/Questions/CreateQuestionForm'
import { CreateQuestionFormValues } from 'modules/ats/components/Questions/QuestionFormValues'
import { questionTypeHasOptions } from 'modules/ats/lib/utils/customQuestion'
import { validateQuestions } from 'modules/ats/lib/utils/questions_validations'

interface Props {
  companyId: number
  onClose: () => void
}

export const CreateQuestionTemplateModal = ({ companyId, onClose }: Props) => {
  const [createQuestionTemplate] = useMutation(AtsCreateQuestionTemplateDocument, {
    extractorFn: (data) => data.atsMutations.createAtsQuestionTemplate,
    refetchQueries: [AtsQuestionTemplatesListDocument],
  })

  const form = useForm<CreateQuestionFormValues>({
    defaultValues: {
      label: '',
      mandatory: false,
      autoDisqualify: false,
      questionType: 'long_text',
      options: [],
    },
    validate(data) {
      return validateQuestions(data, {})
    },
    async onSubmit(values, { setErrors }) {
      const shouldSendOptions = values.questionType
        ? questionTypeHasOptions(values.questionType)
        : false

      const result = await createQuestionTemplate({
        variables: {
          companyId: companyId,
          options: shouldSendOptions ? values.options : null,
          autoDisqualify: values.autoDisqualify,
          label: values.label,
          mandatory: values.mandatory,
          questionType: values.questionType ?? 'long_text',
        },
      })

      const handled = handleResult(result, ({ label, mandatory, questionType }) => {
        setErrors({
          label,
          mandatory,
          questionType,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      illustration="server-edit"
      title={i18n.t('ats.settings.templates.custom_questions.new_question.title')}
      description={i18n.t('ats.settings.templates.custom_questions.new_question.description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button disabled={form.isSubmitting} submit>
          {i18n.t('common.save')}
        </Button>
      }
    >
      <CreateQuestionForm form={form} />
    </Modal>
  )
}
